import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import { CALENDAR_DOW, DRAW_DOW, FIRST_DRAW, MONTHS_VN, WEEKDAYS_VN } from "./constants";

export default function DateFilter({ value, onChange, maxDate, minDate = FIRST_DRAW }) {
  const selected = dayjs(value);
  const max = maxDate ? dayjs(maxDate) : dayjs();
  const min = dayjs(minDate);
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(selected.startOf("month"));

  useEffect(() => {
    const next = dayjs(value);
    if (next.isValid()) setCursor(next.startOf("month"));
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const cells = useMemo(() => {
    const start = cursor.startOf("month");
    const startWeekday = (start.day() + 6) % 7;
    const items = [];
    for (let i = 0; i < startWeekday; i += 1) {
      items.push({ date: start.subtract(startWeekday - i, "day"), outside: true });
    }
    for (let day = 1; day <= cursor.daysInMonth(); day += 1) {
      items.push({ date: start.date(day), outside: false });
    }
    while (items.length % 7 !== 0) {
      items.push({ date: items[items.length - 1].date.add(1, "day"), outside: true });
    }
    return items;
  }, [cursor]);

  const clamp = (d) => {
    if (d.isAfter(max, "day")) return max.format("YYYY-MM-DD");
    if (d.isBefore(min, "day")) return min.format("YYYY-MM-DD");
    return d.format("YYYY-MM-DD");
  };

  const go = (amount, unit) => onChange(clamp(selected.add(amount, unit)));
  const canPrev = selected.isAfter(min, "day");
  const canNext = selected.isBefore(max, "day");
  const canNextMonth = cursor.isBefore(max, "month");
  const canPrevMonth = cursor.isAfter(min, "month");
  const weekday = selected.isValid() ? WEEKDAYS_VN[selected.day()] : "";

  const pick = (key) => {
    onChange(key);
    setOpen(false);
  };

  return (
    <div className="dt636-filter" ref={rootRef}>
      <div className="dt636-filter__bar">
        <button
          type="button"
          className="dt636-filter__jump is-wide"
          aria-label="Kỳ đầu tiên"
          disabled={!canPrev}
          onClick={() => onChange(min.format("YYYY-MM-DD"))}
        >
          &lt;&lt;&lt;&lt;
        </button>
        <button
          type="button"
          className="dt636-filter__jump is-wide"
          aria-label="Lùi 1 tháng"
          disabled={!canPrev}
          onClick={() => go(-1, "month")}
        >
          &lt;&lt;&lt;
        </button>
        <button
          type="button"
          className="dt636-filter__jump"
          aria-label="Lùi 1 tuần"
          disabled={!canPrev}
          onClick={() => go(-1, "week")}
        >
          &lt;&lt;
        </button>
        <button
          type="button"
          className="dt636-filter__jump"
          aria-label="Ngày trước"
          disabled={!canPrev}
          onClick={() => go(-1, "day")}
        >
          &lt;
        </button>

        <button
          type="button"
          className={`dt636-filter__date${open ? " is-open" : ""}`}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {weekday} {selected.format("DD/MM/YYYY")}
        </button>

        <button
          type="button"
          className="dt636-filter__jump"
          aria-label="Ngày sau"
          disabled={!canNext}
          onClick={() => go(1, "day")}
        >
          &gt;
        </button>
        <button
          type="button"
          className="dt636-filter__jump"
          aria-label="Tới 1 tuần"
          disabled={!canNext}
          onClick={() => go(1, "week")}
        >
          &gt;&gt;
        </button>
        <button
          type="button"
          className="dt636-filter__jump is-wide"
          aria-label="Tới 1 tháng"
          disabled={!canNext}
          onClick={() => go(1, "month")}
        >
          &gt;&gt;&gt;
        </button>
        <button
          type="button"
          className="dt636-filter__jump is-wide"
          aria-label="Kỳ mới nhất"
          disabled={!canNext}
          onClick={() => onChange(max.format("YYYY-MM-DD"))}
        >
          &gt;&gt;&gt;&gt;
        </button>
      </div>

      {open ? (
        <div className="dt636-cal">
          <div className="dt636-cal__nav">
            <button
              type="button"
              className="dt636-filter__jump"
              aria-label="Tháng trước"
              disabled={!canPrevMonth}
              onClick={() => setCursor((c) => c.subtract(1, "month"))}
            >
              <ChevronLeftIcon width={12} height={12} />
            </button>
            <div className="dt636-cal__month">
              {MONTHS_VN[cursor.month()]} {cursor.year()}
            </div>
            <button
              type="button"
              className="dt636-filter__jump"
              aria-label="Tháng sau"
              disabled={!canNextMonth}
              onClick={() => canNextMonth && setCursor((c) => c.add(1, "month"))}
            >
              <ChevronRightIcon width={12} height={12} />
            </button>
          </div>
          <div className="dt636-cal__dow">
            {CALENDAR_DOW.map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>
          <div className="dt636-cal__grid">
            {cells.map(({ date, outside }) => {
              const key = date.format("YYYY-MM-DD");
              const disabled = date.isAfter(max, "day") || date.isBefore(min, "day");
              const isSelected = date.isSame(selected, "day");
              const isToday = date.isSame(dayjs(), "day");
              const isDraw = DRAW_DOW.includes(date.day());
              return (
                <button
                  key={key}
                  type="button"
                  disabled={disabled}
                  className={[
                    "dt636-cal__day",
                    outside ? "is-outside" : "",
                    isSelected ? "is-selected" : "",
                    isToday ? "is-today" : "",
                    isDraw && !disabled ? "is-draw" : "",
                  ].join(" ")}
                  onClick={() => pick(key)}
                >
                  {date.date()}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
